import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { tokens } from "../../theme";

const colors = tokens();


export const getFontPack = (theme) => {
  const fontFamily = theme.typography?.fontFamily || "inherit";
  return {
    fontFamily,
    FW_BOLD: theme.typography?.fontWeightBold ?? 700,
    FW_MED: theme.typography?.fontWeightMedium ?? 600,
    FW_REG: theme.typography?.fontWeightRegular ?? 400,
  };
};

export const useCountUp = ({ value = 0, duration = 900, locale = "es-CL" }) => {
  const [displayValue, setDisplayValue] = useState(0);

  const formatted = useMemo(
    () => (Number(displayValue) || 0).toLocaleString(locale),
    [displayValue, locale]
  );

  useEffect(() => {
    let raf;
    const start = performance.now();
    const target = Number(value) || 0;

    const animate = (now) => {
      const p = Math.min((now - start) / duration, 1);
      setDisplayValue(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return { displayValue, formatted };
};


export const getTitleSx = ({ titleWrap = false, fontFamily, withFamily = true }) => {
  const base = titleWrap
    ? {
      lineHeight: 1.15,
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "unset",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
    }
    : {
      lineHeight: 1.05,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

  return withFamily ? { fontFamily, ...base } : base;
};

export const resolveCountLabel = (label, value) => {
  if (
    !label ||
    typeof label !== "object" ||
    Array.isArray(label)
  ) {
    return label ?? "";
  }

  return Number(value) === 1
    ? label.singular ?? label.plural ?? ""
    : label.plural ?? label.singular ?? "";
};

export const TargetMedia = ({
  imgSrc,
  icon,
  size = 44,
  title = "",
  imgFit = "cover",
  imgRound = true,
  border = colors.grey[700],
}) => {
  if (imgSrc) {
    return (
      <Box
        component="img"
        src={imgSrc}
        alt={title}
        loading="lazy"
        decoding="async"
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          border: `1px solid ${border}`,
          backgroundColor: colors.grey[400],
          overflow: "hidden",
          borderRadius: imgRound ? "50%" : "12px",
          objectFit: imgFit,
          p: imgFit === "contain" ? 0.6 : 0,
          boxSizing: "border-box",
        }}
      />
    );
  }

  if (icon) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          border: `1px solid ${border}`,
          backgroundColor: colors.grey[100],
          overflow: "hidden",
          "& svg": { width: "68%", height: "68%", fontSize: "unset !important" },
        }}
      >
        {icon}
      </Box>
    );
  }

  return null;
};

export const ValueWithLabel = ({
  formatted,
  valueLabel = "",
  variant = "h4",
  fontFamily,
  fontWeight = 700,
  color,
  sx,
}) => {
  return (
    <Typography
      component="div"
      variant={variant}
      fontWeight={fontWeight}
      color={color}
      sx={{
        fontFamily,
        lineHeight: 1.1,
        ...(sx || {}),
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          columnGap: 0.8,
          rowGap: 0.15,
          maxWidth: "100%",
        }}
      >
        <Box component="span">{formatted}</Box>

        {!!valueLabel && (
          <Typography
            component="span"
            sx={{
              fontFamily,
              fontWeight,
              opacity: 0.9,
              fontSize:
                variant === "h2"
                  ? "0.60em"
                  : variant === "h3"
                    ? "0.65em"
                    : "0.70em",
              lineHeight: 1.1,
              whiteSpace: "nowrap",
            }}
          >
            {valueLabel}
          </Typography>
        )}
      </Box>
    </Typography>
  );
};

